import React from 'react'
import styled, { css } from 'styled-components'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'

const PreviewHoverOverlay = css`
    /* Base Inactive Styling */
    filter: ${({ $isActive, $isDisabled }) => ($isActive && !$isDisabled) ? 'none' : 'grayscale(100%) brightness(0.6)'};
    opacity: ${({ $isActive, $isDisabled }) => ($isActive && !$isDisabled) ? 1 : 0.95} !important;
    background: ${({ $isActive, $isDisabled }) =>
        ($isActive && !$isDisabled) ? 'rgba(73, 74, 181, 0.20)' : 'rgba(47, 42, 57, 0.2)'} !important;
    border: 1px solid
        ${({ $isActive, $isDisabled }) =>
        ($isActive && !$isDisabled) ? 'rgba(99, 81, 219, 0.6)' : 'rgba(203, 182, 255, 0.1)'} !important;
    box-shadow: ${({ $isActive, $isDisabled }) =>
        ($isActive && !$isDisabled) ? '0 0 20px rgba(99, 102, 241, 0.1)' : 'none'};


    /* "CLICK" Focus Overlay */
    &::before {
        content: '';
        position: absolute;
        inset: 0;
        background: radial-gradient(circle, rgba(99, 102, 241, 0.2) 0%, rgba(0, 0, 0, 0.85) 100%);
        opacity: 0;
        z-index: 10;
        pointer-events: none;
        transition: opacity 0.3s ease;
    }

    /* "GO TO MODULE" Bottom Bar */
    &::after {
        content: '${({ $label }) => $label || 'VIEW MODULE'}';
        position: absolute;
        bottom: 8px;
        left: 50%;
        transform: translateX(-50%) translateY(20px);
        background: rgba(99, 102, 241, 0.5);
        backdrop-filter: blur(10px);
        padding: 4px 12px;
        border-radius: 4px;
        border: 1px solid rgba(255, 255, 255, 0.2);
        color: #fff;
        font-size: 9px;
        font-weight: 700;
        letter-spacing: 1px;
        font-family: var(--font-chakra);
        text-transform: uppercase;
        opacity: 0;
        z-index: 20;
        transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
        white-space: nowrap;
    }

    ${({ $isDisabled }) => !$isDisabled ? css`
        cursor: pointer;
        &:hover {
            transform: translateY(-2px);
            border-color: rgba(99, 81, 219, 0.5) !important;
            box-shadow: 0 8px 25px rgba(0, 0, 0, 0.5), 0 0 15px rgba(99, 81, 219, 0.2) !important;
            opacity: 1 !important;
            filter: none !important;

            &::before { opacity: 1; }
            &::after { 
                opacity: 1;
                transform: translateX(-50%) translateY(0);
            }
            
            .module-toggle {
                opacity: 1;
                transform: translate(0, 0);
            }
        }
    ` : css`
        pointer-events: none;
        cursor: default;
    `}
`

const CardContainer = styled.div`
    grid-column: span 1;
    grid-row: span 1;
    cursor: pointer;
    transition: all 0.3s ease;
    border-radius: 12px;
    overflow: hidden;
    position: relative;
    height: 100%;
    //background: linear-gradient(135deg, rgba(0, 0, 0, 0.3) 0%, rgba(30, 30, 50, 0.2) 100%);
    padding: 12px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 0;

    ${PreviewHoverOverlay}
`

const ToggleCorner = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    width: 65px;
    height: 65px;
    overflow: hidden;
    z-index: 30;
    pointer-events: none;
    
    /* Default Hidden */
    opacity: 0;
    transform: translate(10px, -10px);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`

const ToggleRibbon = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    width: 100%;
    height: 100%;
    /* Triangle Shape top-right */
    clip-path: polygon(100% 0, 0 0, 100% 100%);
    
    background: ${({ $isActive, $isDisabled }) =>
        ($isActive && !$isDisabled)
            ? 'linear-gradient(225deg, #ff00ddff 0%, rgba(255, 0, 187, 0.8) 50%, transparent 50%)'
            : 'linear-gradient(225deg, #444 0%, rgba(85, 85, 85, 0.9) 50%, transparent 50%)'};
            
    cursor: ${({ $isDisabled }) => $isDisabled ? 'default' : 'pointer'};
    pointer-events: ${({ $isDisabled }) => $isDisabled ? 'none' : 'auto'};
    transition: all 0.2s ease;
    box-shadow: -2px 2px 10px rgba(0,0,0,0.3);

    &:hover {
        background: ${({ $isActive, $isDisabled }) =>
        ($isActive && !$isDisabled)
            ? 'linear-gradient(225deg, #ff1aee 0%, #ff00ddff 50%, rgba(255, 0, 221, 0.5) 50%)'
            : 'linear-gradient(225deg, rgba(139, 92, 246, 0.7) 0%, rgba(99, 102, 241, 0.9) 50%, rgba(99, 102, 241, 0.3) 50%)'};
        transform: scale(1.05); 
        transform-origin: top right;
        box-shadow: ${({ $isActive, $isDisabled }) =>
        ($isActive && !$isDisabled)
            ? '-2px 2px 15px rgba(255, 0, 221, 0.6)'
            : '-2px 2px 15px rgba(99, 102, 241, 0.5)'};
    }
`

const ToggleRibbonContent = styled.div`
    position: absolute;
    top: 8px;
    right: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0px;
    z-index: 21;
`

const ToggleStatusText = styled.div`
    font-size: 9px;
    font-weight: 800;
    text-transform: uppercase;
    color: white;
    font-family: var(--font-chakra);
    margin-right: 2px;
`

const FloatingToggle = ({ isActive, onToggle, isDisabled }) => (
    <ToggleCorner className="module-toggle">
        <ToggleRibbon
            $isActive={isActive}
            $isDisabled={isDisabled}
            onClick={(e) => {
                e.stopPropagation()
                onToggle()
            }}
        >
            <ToggleRibbonContent>
                <CheckCircleIcon style={{
                    fontSize: 18,
                    color: '#fff',
                    opacity: isActive ? 1 : 0.4,
                    marginBottom: 2
                }} />
                <ToggleStatusText style={{ opacity: isActive ? 1 : 0.4 }}>
                    {isActive ? 'ON' : 'OFF'}
                </ToggleStatusText>
            </ToggleRibbonContent>
        </ToggleRibbon>
    </ToggleCorner>
)

export default function PreviewCard({
    isActive,
    isDisabled, // usually !configName
    onToggle,
    onClick,
    label,
    children,
    className,
    style,
    hideToggle = false
}) {
    return (
        <CardContainer
            $isActive={isActive}
            $isDisabled={isDisabled}
            onClick={onClick}
            $label={label}
            className={className}
            style={style}
        >
            {!hideToggle && (
                <FloatingToggle
                    isActive={isActive}
                    isDisabled={isDisabled}
                    onToggle={onToggle}
                />
            )}
            {children}
        </CardContainer>
    )
}
